import IconEmojiSparkles from "@shared/components/icons/EmojiSparkles"
import { ArrowUpRight } from "@untitledui/icons"
import { motion, useReducedMotion } from "motion/react"

/**
 * The card menu's answer to turning "Show Card on Class Website" on: the success toast's own
 * sparkles and mint, because this IS that moment — the toggle has no other applause, and a
 * gray line of help text reads like a warning label instead of a win.
 *
 * The link is the "here's how to use it" half, and it is the only place in the editor that
 * opens this lesson as a student sees it. Without a url (a template or a unit, which belong
 * to no one day) the callout still lands; there is just nothing to open.
 */
export function CardPublicCallout({ studentLessonUrl }: { studentLessonUrl?: string }) {
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      // The callout owns its own collapse. Nothing above it animates the menu's height, so
      // fading out while holding full height leaves the space to snap shut on unmount —
      // shrinking to 0 here IS the menu closing the gap, in the same 280ms as the fade.
      // overflow-hidden both clips the content mid-collapse and keeps the inner margins from
      // escaping the measured height.
      className="overflow-hidden"
      initial={reduceMotion ? false : { height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Inset to the row's own padding, so the panel lines up with the labels above it.
          The asymmetric margins are the grouping: tucked up under the row it answers (the
          negative margin eats half that row's bottom pad) and held off the next one. Equal
          gaps would read as a third thing floating between two rows. */}
      <motion.div
        data-test="card public callout"
        className="mx-[var(--menu-row-inset,1.75rem)] -mt-1 mb-2 flex items-start gap-2.5 rounded-xl bg-success-primary px-3 py-2.5"
        initial={reduceMotion ? false : { y: -8, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        // Barely-there overshoot: the wrapper clips at the box's own edges, so a springier
        // scale would shave its top and bottom on the way past 1.
        transition={{ type: "spring", stiffness: 420, damping: 26 }}
      >
        <motion.span
          className="mt-px inline-flex size-6 shrink-0"
          initial={reduceMotion ? false : { scale: 0, rotate: -35 }}
          animate={{ scale: 1, rotate: 0 }}
          // The one place with room to actually bounce — it lands after the mint does, so the
          // sparkles read as the reaction to the box arriving rather than part of it.
          transition={{ type: "spring", stiffness: 600, damping: 12, delay: 0.08 }}
        >
          <IconEmojiSparkles className="size-6" />
        </motion.span>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="m-0 text-[0.9375rem] leading-tight font-bold text-black">
            It’s on your class website!
          </p>
          <p className="m-0 text-[0.8125rem] leading-snug text-gray-700">
            Students see this card whenever they visit.
          </p>
          {studentLessonUrl === undefined ? null : (
            <a
              data-test="student version link"
              href={studentLessonUrl}
              target="_blank"
              rel="noreferrer"
              className="text-link-blue mt-0.5 inline-flex w-fit items-center gap-1 text-[0.8125rem] leading-none font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 data-focus-visible:outline-focus-ring"
            >
              {/* The link opens this one lesson as a student sees it, not the class website's
                  front door — and it is named, not "take a look", because a screen reader reads
                  link text out of context. */}
              See the student version
              <ArrowUpRight className="size-3.5 shrink-0" />
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}
